import React, {FC, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {downloadBulkTemplate} from '../../add-bulk/core/_requests'
import {UserModel} from '../../../auth'

type IProps = {
  id: string
  hasError?: boolean
  errorMsg?: string
  templateUrl: string
  token?: string
  user: UserModel | undefined
  setField: React.Dispatch<any>
  // Optional async file check (e.g. reject a barcode-only update sheet).
  // Returns an error message to show, or null when the file is valid.
  validator?: (file: File) => Promise<string | null>
}

// Excel only — mirrors the accept list used by the shared BulkUploadField.
const EXCEL_ACCEPT =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'

/**
 * Steps 1 & 2 of the Add Discount - Bulk redesign (template download +
 * drag/drop upload). The file-handling and template-download logic is the
 * same as the shared BulkUploadField; only the presentation differs so the
 * shared component stays untouched for the other bulk views.
 */
const BulkDropzone: FC<IProps> = ({id, hasError, errorMsg, templateUrl, token = '', user, setField, validator}) => {
  const {formatMessage} = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [validationErr, setValidationErr] = useState<string | null>(null)
  const [downloadErr, setDownloadErr] = useState<string | null>(null)

  const getTemplate = async () => {
    setDownloadErr(null)
    try {
      await downloadBulkTemplate(
        templateUrl,
        token,
        `${user?.name}_${new Date().toISOString().slice(0, 10)}.xlsx`,
      )
    } catch {
      // Saving whatever came back used to leave the supplier with a file Excel
      // rejects and no idea why (DEV-3787) — say so on the step that failed.
      setDownloadErr(formatMessage({id: 'template_download_failed'}))
    }
  }

  const acceptFile = async (file?: File | null) => {
    setValidationErr(null)
    if (file) {
      if (validator) {
        const err = await validator(file)
        if (err) {
          setFileName(null)
          setValidationErr(err)
          setField({error: true, value: ''})
          return
        }
      }
      setFileName(file.name)
      setField({error: false, value: file})
    } else {
      setFileName(null)
      setField({error: true, value: ''})
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.currentTarget.files?.[0] ?? null)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    acceptFile(e.dataTransfer.files?.[0] ?? null)
  }

  const openPicker = () => inputRef.current?.click()

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openPicker()
    }
  }

  return (
    <div className='adb-card-inner'>
      {/* Step 1 — download template */}
      <div className='adb-step'>
        <div className='adb-step-num'>1</div>
        <div className='adb-step-body'>
          <h2 className='adb-step-title'>{formatMessage({id: 'download_template_title'})}</h2>
          <p className='adb-step-desc'>{formatMessage({id: 'download_template_desc'})}</p>
          <div className='adb-download-row'>
            <button type='button' className='adb-btn-download' onClick={getTemplate}>
              <i className='fas fa-download' aria-hidden='true' />
              {formatMessage({id: 'download_template_button'})}
            </button>
            <span className='adb-warn'>
              <i className='fas fa-triangle-exclamation' aria-hidden='true' />
              {formatMessage({id: 'new_bulk_template'})}
            </span>
          </div>
          {downloadErr && (
            <span className='adb-error' role='alert'>
              {downloadErr}
            </span>
          )}
        </div>
      </div>

      <div className='adb-divider' />

      {/* Step 2 — upload bulk sheet */}
      <div className='adb-step'>
        <div className='adb-step-num'>2</div>
        <div className='adb-step-body'>
          <h2 className='adb-step-title'>
            {formatMessage({id: 'upload_bulk_sheet_title'})} <sup className='text-danger'>*</sup>
          </h2>
          <p className='adb-step-desc'>{formatMessage({id: 'upload_bulk_sheet_desc'})}</p>

          <div
            role='button'
            tabIndex={0}
            className={`adb-dropzone${isDragOver ? ' is-dragover' : ''}${hasError ? ' is-invalid' : ''}`}
            onClick={openPicker}
            onKeyDown={handleKeyDown}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragOver(true)
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            <div className='adb-dropzone-icon'>
              <i className='fas fa-cloud-arrow-up' aria-hidden='true' />
            </div>
            <p className='adb-dropzone-title'>{formatMessage({id: 'click_to_upload_drag_drop'})}</p>
            <p className='adb-dropzone-hint'>{formatMessage({id: 'bulk_upload_hint'})}</p>
            {fileName && (
              <span className='adb-file-name'>
                <i className='fas fa-circle-check' aria-hidden='true' />
                {fileName}
              </span>
            )}
            <input
              ref={inputRef}
              type='file'
              id={id}
              accept={EXCEL_ACCEPT}
              onChange={handleInputChange}
            />
          </div>

          {(hasError || validationErr) && (
            <span className='adb-error'>
              {validationErr || errorMsg || formatMessage({id: 'please_upload_bulk'})}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default BulkDropzone
